import { PaywallResult, Plan } from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Status } from '#/components/Status';
import { SignupStepIndicator } from '#/components/StepIndicator/SignupStepIndicator';
import { useConfig, useNavigation } from '#/hooks';
import { AppStatus } from '#/service/constants';
import { Path } from '#/service/routes';
import { noop, remify } from '#/service/utils';
import { useAuthentication } from '#/store/auth';

import { Logo, LogoContainer } from '../Logo';
import { PlanPurchaseFlowProps } from './props';
import PickPlanStep from './steps/PickPlanStep/PickPlanStep';
import Payment from './steps/PurchasePlanStep/PurchasePlanStep';
import { Step } from './types';

const defaultSteps: Step[] = [
  {
    header: 'iap.selectYourPlan',
    Component: PickPlanStep,
  },
  {
    header: 'iap.setUpYourPayment',
    Component: Payment,
  },
];

const PlanPickerContainer = styled.div`
  display: grid;
  justify-items: center;
  align-content: start;
  row-gap: ${remify(45)};
  text-align: left;
  margin: ${remify(80)} ${remify(30)} ${remify(75)};
`;

const PlanPickerHeader = styled.h2`
  font: var(--fontStyleHeadline);
  color: var(--pageHeadingForeground);
  margin: 0;
  text-align: center;
  max-width: 80vw;
`;

const PlanPickerStepContainer = styled.div`
  width: 100%;
  max-width: ${remify(450)};
`;

const PlanPurchaseFlow = ({
  plans,
  actions,
  steps = defaultSteps,
  hasError,
  customParameters,
  onResult = noop,
}: PlanPurchaseFlowProps) => {
  const [plan, setPlan] = useState<Plan>();
  const { awaitingEntitlements } = useAuthentication();
  const [payment, setPayment] = useState<{
    result: boolean;
    isExternal?: boolean;
  }>();
  const [step, setStep] = useState(0);
  const { pathname } = useLocation();
  const { goHome } = useNavigation('PlanPurchaseFlow');
  const containerRef = useRef<HTMLDivElement>(null);
  const { featureConfig } = useConfig();
  const { successfulPurchaseBackground } = featureConfig.purchase.images ?? {};

  useEffect(() => {
    if (!plans.length) {
      actions.handleError({
        title: t('iap.emptyState.title'),
        message: t('iap.emptyState.message'),
      });
    }
  }, [plans.length, actions]);

  // scroll all parents, including the full page modal to the top on step change
  useEffect(() => {
    let element = containerRef.current?.parentElement;

    while (element) {
      element.scrollTop = 0;
      element = element.parentElement;
    }
  }, [step]);

  if (hasError || !plans.length || step >= steps.length) {
    return null;
  }

  const handlePlanChange = (selectedPlan: Plan) => {
    const { vendorTag } = customParameters ?? {};
    // we need to make a copy, so we can extend customParameters despite valtio.
    setPlan({
      ...selectedPlan,
      customParameters: {
        ...selectedPlan.customParameters,
        ...(vendorTag && { vendorTag }),
      },
    });
  };

  if (payment) {
    if (payment.isExternal) {
      return (
        <Status
          onDismiss={actions.handleClose}
          status={AppStatus.Loading}
          statusMessage={t('web.iap.external.success.message')}
          statusTitle={t('iap.loading.title')}
        />
      );
    }

    if (awaitingEntitlements) {
      return (
        <Status
          status={AppStatus.Loading}
          statusMessage={t('iap.loading.message')}
          statusTitle={t('iap.loading.title')}
        />
      );
    }

    return (
      <Status
        applyDefaultGradient={false}
        backgroundImageUrl={successfulPurchaseBackground}
        dismissMessage={t('iap.success.button')}
        onDismiss={actions.handleClose}
        status={AppStatus.Success}
        statusMessage={t('iap.success.message')}
        statusTitle={t('iap.success.title')}
      />
    );
  }

  const { header, Component } = steps[step];
  const isOnRegistrationPage = pathname === Path.Registration;
  // `step` is 0-based, but the indicator's `currentStep` is 1-based.
  // and we need to add 1 because the registration step is already done.
  const indicatorCurrentStep = step + 2;

  return (
    <>
      {!customParameters?.vendorTag && (
        <LogoContainer>
          <Logo onClick={goHome} />
        </LogoContainer>
      )}
      <PlanPickerContainer ref={containerRef}>
        {isOnRegistrationPage && (
          <SignupStepIndicator currentStep={indicatorCurrentStep} />
        )}
        <PlanPickerHeader data-testid="plan-picker-header">
          {t(header)}
        </PlanPickerHeader>
        <PlanPickerStepContainer>
          <Component
            actions={actions}
            customParameters={customParameters}
            nextStep={() => setStep(step + 1)}
            onPaymentChange={({ isSuccessful, isExternal }) => {
              setPayment({ result: isSuccessful, isExternal: isExternal });
              onResult(
                isSuccessful ? PaywallResult.Payed : PaywallResult.Aborted
              );
            }}
            onPlanChange={handlePlanChange}
            plans={plans}
            previousStep={() => setStep(step - 1)}
            selectedPlan={plan}
          />
        </PlanPickerStepContainer>
      </PlanPickerContainer>
    </>
  );
};

PlanPurchaseFlow.displayName = 'PlanPurchaseFlow';

export default PlanPurchaseFlow;
